"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { SubmissionStep } from "./types/Steps";
import AgreementStep from "./steps/AgreementStep";
import ArtworkStep from "./steps/ArtworkStep";
import { useArtworkSubmissionForm } from "./hooks/useArtworkSubmissionForm";
import { useArtworkSubmissionMutation } from "./hooks/useArtworkSubmissionMutation";
import { SubmissionPhase } from "./ui/SubmissionProgress";
import { ApiWave } from "@/generated/models/ApiWave";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
interface MemesArtSubmissionContainerProps {
  readonly onClose: () => void;
  readonly wave: ApiWave;
}

/**
 * MemesArtSubmissionContainer - Main container component for the artwork submission flow
 *
 * This component has been simplified by:
 * 1. Moving form state management to a custom hook with reducer pattern
 * 2. Extracting the modal layout to a separate component
 * 3. Using an enum with a component map for cleaner step routing
 * 4. Using direct component composition instead of component injection
 * 5. Using a separate mutation hook for API submission
 * 6. Using a dedicated progress component for visual feedback
 */
const MemesArtSubmissionContainer: React.FC<MemesArtSubmissionContainerProps> = t0 => {
  const $ = _c(64);
  const {
    onClose,
    wave
  } = t0;
  const form = useArtworkSubmissionForm();
  const {
    isSafeWallet,
    address
  } = useSeizeConnectContext();
  const {
    submitArtwork,
    uploadProgress,
    submissionPhase,
    submissionError,
    isSubmitting
  } = useArtworkSubmissionMutation();
  let t1;
  let t2;
  if ($[0] !== onClose || $[1] !== submissionPhase) {
    t1 = () => {
      if (submissionPhase === "success") {
        const timer = setTimeout(() => {
          onClose();
        }, 1200);
        return () => clearTimeout(timer);
      }
    };
    t2 = [submissionPhase, onClose];
    $[0] = onClose;
    $[1] = submissionPhase;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== form) {
    t3 = file => {
      form.handleFileSelect(file);
    };
    $[4] = form;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleFileSelect = t3;
  const handlePhaseChange = _temp;
  let t4;
  if ($[6] !== address || $[7] !== form || $[8] !== isSafeWallet || $[9] !== submitArtwork || $[10] !== wave) {
    t4 = async () => {
      const {
        traits
      } = form.getSubmissionData();
      const media = form.getMediaSelection();
      if (media.mediaSource === "upload") {
        if (!media.selectedFile) {
          return null;
        }
        return submitArtwork({
          imageFile: media.selectedFile,
          traits,
          waveId: wave.id,
          termsOfService: wave.participation.terms
        }, address ?? "", isSafeWallet, {
          onPhaseChange: handlePhaseChange
        });
      }
      if (!media.isExternalValid) {
        return null;
      }
      return submitArtwork({
        externalMedia: {
          url: media.externalUrl,
          mimeType: media.externalMimeType
        },
        traits,
        waveId: wave.id,
        termsOfService: wave.participation.terms
      }, address ?? "", isSafeWallet, {
        onPhaseChange: handlePhaseChange
      });
    };
    $[6] = address;
    $[7] = form;
    $[8] = isSafeWallet;
    $[9] = submitArtwork;
    $[10] = wave;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleSubmit = t4;
  let t5;
  if ($[12] !== form.selectedFile) {
    t5 = form.selectedFile ? {
      name: form.selectedFile.name,
      size: form.selectedFile.size
    } : null;
    $[12] = form.selectedFile;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const fileInfo = t5;
  let t6;
  if ($[14] !== form.agreements || $[15] !== form.handleContinueFromTerms || $[16] !== form.setAgreements || $[17] !== wave) {
    t6 = <AgreementStep wave={wave} agreements={form.agreements} setAgreements={form.setAgreements} onContinue={form.handleContinueFromTerms} />;
    $[14] = form.agreements;
    $[15] = form.handleContinueFromTerms;
    $[16] = form.setAgreements;
    $[17] = wave;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== fileInfo || $[20] !== form.artworkUploaded || $[21] !== form.artworkUrl || $[22] !== form.clearExternalMedia || $[23] !== form.externalMediaError || $[24] !== form.externalMediaHashInput || $[25] !== form.externalMediaMimeType || $[26] !== form.externalMediaPreviewUrl || $[27] !== form.externalMediaProvider || $[28] !== form.externalMediaUrl || $[29] !== form.externalMediaValidationStatus || $[30] !== form.isExternalMediaValid || $[31] !== form.mediaSource || $[32] !== form.setArtworkUploaded || $[33] !== form.setExternalMediaHash || $[34] !== form.setExternalMediaMimeType || $[35] !== form.setExternalMediaProvider || $[36] !== form.setMediaSource || $[37] !== form.setTraits || $[38] !== form.traits || $[39] !== form.updateTraitField || $[40] !== handleFileSelect || $[41] !== handleSubmit || $[42] !== isSubmitting || $[43] !== onClose || $[44] !== submissionError || $[45] !== submissionPhase || $[46] !== uploadProgress) {
    t7 = <ArtworkStep traits={form.traits} artworkUploaded={form.artworkUploaded} artworkUrl={form.artworkUrl} setArtworkUploaded={form.setArtworkUploaded} handleFileSelect={handleFileSelect} mediaSource={form.mediaSource} setMediaSource={form.setMediaSource} externalHash={form.externalMediaHashInput} externalProvider={form.externalMediaProvider} externalConstructedUrl={form.externalMediaUrl} externalPreviewUrl={form.externalMediaPreviewUrl} externalMimeType={form.externalMediaMimeType} externalError={form.externalMediaError} externalValidationStatus={form.externalMediaValidationStatus} isExternalMediaValid={form.isExternalMediaValid} onExternalHashChange={form.setExternalMediaHash} onExternalProviderChange={form.setExternalMediaProvider} onExternalMimeTypeChange={form.setExternalMediaMimeType} onClearExternalMedia={form.clearExternalMedia} onSubmit={handleSubmit} onCancel={onClose} updateTraitField={form.updateTraitField} setTraits={form.setTraits} isSubmitting={isSubmitting} submissionPhase={submissionPhase} uploadProgress={uploadProgress} fileInfo={fileInfo} submissionError={submissionError} />;
    $[19] = fileInfo;
    $[20] = form.artworkUploaded;
    $[21] = form.artworkUrl;
    $[22] = form.clearExternalMedia;
    $[23] = form.externalMediaError;
    $[24] = form.externalMediaHashInput;
    $[25] = form.externalMediaMimeType;
    $[26] = form.externalMediaPreviewUrl;
    $[27] = form.externalMediaProvider;
    $[28] = form.externalMediaUrl;
    $[29] = form.externalMediaValidationStatus;
    $[30] = form.isExternalMediaValid;
    $[31] = form.mediaSource;
    $[32] = form.setArtworkUploaded;
    $[33] = form.setExternalMediaHash;
    $[34] = form.setExternalMediaMimeType;
    $[35] = form.setExternalMediaProvider;
    $[36] = form.setMediaSource;
    $[37] = form.setTraits;
    $[38] = form.traits;
    $[39] = form.updateTraitField;
    $[40] = handleFileSelect;
    $[41] = handleSubmit;
    $[42] = isSubmitting;
    $[43] = onClose;
    $[44] = submissionError;
    $[45] = submissionPhase;
    $[46] = uploadProgress;
    $[47] = t7;
  } else {
    t7 = $[47];
  }
  let t8;
  if ($[48] !== t6 || $[49] !== t7) {
    t8 = {
      [SubmissionStep.AGREEMENT]: t6,
      [SubmissionStep.ARTWORK]: t7
    };
    $[48] = t6;
    $[49] = t7;
    $[50] = t8;
  } else {
    t8 = $[50];
  }
  const stepComponents = t8;
  let t9;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="tw-absolute tw-inset-0 tw-rounded-xl tw-overflow-hidden"><div className="tw-absolute tw-w-1/2 tw-h-1/2 tw-bg-primary-500/[0.03] tw-blur-3xl -tw-top-1/4 -tw-right-1/4" /><div className="tw-absolute tw-w-2/3 tw-h-1/2 tw-bg-purple-500/[0.02] tw-blur-3xl tw-top-1/4 -tw-left-1/4" /><div className="tw-absolute tw-w-1/2 tw-h-1/2 tw-bg-iron-500/[0.03] tw-blur-3xl -tw-bottom-1/4 -tw-left-1/4" /></div>;
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  const t10 = `tw-flex tw-justify-between tw-w-full lg:tw-mb-0 tw-pt-6 lg:tw-pt-8 tw-pb-4 lg:tw-pb-6 tw-flex-shrink-0 tw-border tw-border-solid tw-border-x-0 tw-border-t-0 lg:tw-border-b-0 tw-border-iron-800 ${form.currentStep === SubmissionStep.AGREEMENT ? "tw-max-w-6xl tw-mx-auto" : ""}`;
  let t11;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <motion.h3 className="tw-text-2xl lg:tw-text-3xl tw-font-semibold tw-text-iron-100">Submit Work to The Memes</motion.h3>;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <FontAwesomeIcon icon={faXmark} className="tw-size-5 tw-flex-shrink-0" />;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== onClose) {
    t13 = <motion.button onClick={onClose} className="tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-size-9 lg:tw-size-10 tw-rounded-full tw-border-0 tw-ring-1 tw-ring-iron-700 tw-bg-transparent tw-text-iron-300 desktop-hover:hover:tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out" aria-label="Close modal">{t12}</motion.button>;
    $[54] = onClose;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== t10 || $[57] !== t13) {
    t14 = <div className="tw-px-4 md:tw-px-8"><div className={t10}>{t11}{t13}</div></div>;
    $[56] = t10;
    $[57] = t13;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  const t15 = stepComponents[form.currentStep];
  let t16;
  if ($[59] !== t15) {
    t16 = <div className="tw-flex-1 tw-min-h-0">{t15}</div>;
    $[59] = t15;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== t14 || $[62] !== t16) {
    t17 = <div className="tw-h-full tw-flex tw-flex-col"><div className="tw-h-full tw-bg-iron-950 tw-rounded-xl tw-relative tw-border tw-border-iron-800 tw-backdrop-blur tw-flex tw-flex-col tw-overflow-hidden">{t9}<div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-h-full">{t14}{t16}</div></div></div>;
    $[61] = t14;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  return t17;
};
export default MemesArtSubmissionContainer;
function _temp(phase) {
  console.log(`Submission phase changed to: ${phase}`);
}